import defaultDocumentSdkManager from "./DocumentSdkManager";

export const DEFAULT_DETECT_DOCUMENT_SCORE_THRESHOLD = 75;
export const DEFAULT_DETECT_DOCUMENT_QUALITY_THRESHOLD = 60;

export class DocumentProcessingError extends Error {
  // `diagnostic` is optional, log-safe detail for diagnostics (#244): a reason
  // name plus a Scanbot status or counts, never document text. It does not
  // change what the user sees.
  constructor(message, canRetry, diagnostic = null) {
    super(message);
    this.name = "DocumentProcessingError";
    this.canRetry = canRetry;
    this.diagnostic = diagnostic;
  }
}

// Every detection failure uses the RN SDK's reason name; the Scanbot status
// says which detection rule failed.
const detectionDiagnostic = (result) => ({
  reason: "no_document_in_still",
  detectionStatus: typeof result?.detectionStatus === "string" ? result.detectionStatus : null,
  detectSuccess: result?.success === true,
});

export const getDocumentDetectionError = (result) => {
  if (result?.success !== true) {
    return new DocumentProcessingError(
      "We couldn't process this photo. Please retake it with the full document visible in the frame.",
      false,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "NotAcquired" || result.detectionStatus === "Error_NothingDetected") {
    return new DocumentProcessingError(
      "We couldn't find the document in the photo. Hold your document steady and retake the photo, making sure all four corners are visible.",
      false,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "OK_BadAngles") {
    return new DocumentProcessingError(
      "The document is tilted. Hold your phone parallel to the document and retake the photo.",
      true,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "Error_Brightness") {
    return new DocumentProcessingError(
      "The lighting is uneven (too dark or too bright). Move to a well-lit area and avoid glare or shadows, then try again.",
      true,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "Error_Noise") {
    return new DocumentProcessingError(
      "The photo is blurry. Hold your phone steady, tap to focus, and retake the photo.",
      true,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "OK_OffCenter") {
    return new DocumentProcessingError(
      "The document is not centered. Move it to the middle of the frame and try again.",
      true,
      detectionDiagnostic(result)
    );
  }

  if (result.detectionStatus === "OK_SmallSize") {
    return new DocumentProcessingError(
      "The document is too far away. Move your camera closer so the document fills most of the frame.",
      true,
      detectionDiagnostic(result)
    );
  }

  return null;
};

const toJpegBlob = (buffer) => {
  return new Blob([buffer], { type: "image/jpeg" });
};

const devResultByImageBuffer = new WeakMap();

const processUploadedDocumentPhotoImpl = async ({
  sdkManager = defaultDocumentSdkManager,
  imageBuffer,
  preprocess,
  postprocess,
  buildResult,
}) => {
  if (!imageBuffer) {
    throw new DocumentProcessingError("We couldn't load your photo. Please choose another photo and try again.", false);
  }

  return await sdkManager.withSdk(async (sdk) => {
    const ctx = {
      sdk,
      imageBuffer,
      originalImageBuffer: imageBuffer,
      detectionResult: null,
      croppedImageBuffer: null,
      artifacts: {},
    };

    if (preprocess) {
      const maybeUpdated = await preprocess(ctx);
      if (maybeUpdated) {
        ctx.imageBuffer = maybeUpdated;
      }
    }

    const detectionResult = await sdk.detectDocument(
      ctx.imageBuffer,
      DEFAULT_DETECT_DOCUMENT_SCORE_THRESHOLD,
      DEFAULT_DETECT_DOCUMENT_QUALITY_THRESHOLD
    );

    ctx.detectionResult = detectionResult;

    const detectionError = getDocumentDetectionError(detectionResult);
    if (detectionError) {
      throw detectionError;
    }

    ctx.croppedImageBuffer = await sdk.cropAndRotateImageCcw(ctx.imageBuffer, detectionResult.polygon, 0);

    if (postprocess) {
      await postprocess(ctx);
    }

    const resultPayload = buildResult
      ? await buildResult(ctx)
      : {
          croppedImageBlob: toJpegBlob(ctx.croppedImageBuffer),
          originalImageBlob: toJpegBlob(ctx.originalImageBuffer),
        };

    return {
      ctx,
      result: resultPayload,
    };
  });
};

export const processUploadedDocumentPhoto = async (args) => {
  const isDev = process.env.NODE_ENV !== "production";
  const imageBuffer = args?.imageBuffer;
  const canDedupe = typeof imageBuffer === "object" && imageBuffer !== null;

  if (!isDev || !canDedupe) {
    return await processUploadedDocumentPhotoImpl(args);
  }

  const existing = devResultByImageBuffer.get(imageBuffer);
  if (existing) {
    return await existing;
  }

  const promise = processUploadedDocumentPhotoImpl(args);
  devResultByImageBuffer.set(imageBuffer, promise);

  try {
    return await promise;
  } finally {
    devResultByImageBuffer.delete(imageBuffer);
  }
};
